import Image from "next/image";

// ui
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@repo/ui/components/ui/card";
import { Body } from "@repo/ui/components/ui/text";
import { cn } from "@repo/ui/lib/utils";
import { Badge } from "@repo/ui/components/ui/badge";
import { Ban } from "lucide-react";

// components
import { ContactCard } from "./contact/contact-card";

// hooks
import { usePublicCompany } from "@/hooks/usePublicCompany";
import { getValidCompanyUrl } from "@/utils/getValidCompanyUrl";

// NOTE: This will display the company info on the left (desktop), bottom (mobile)
// and the content on the right (desktop), top (mobile).
// If no companyId is provided, it will display the right content (desktop), top (mobile) in the middle of the screen.

const BG_BY_INTENT = {
  neutral: "bg-custom-base-subtle",
  danger: "bg-red-50",
  success: "bg-green-50",
  warn: "bg-yellow-50"
};
export function Layout5050({
  children,
  companyId,
  cardInfo,
  variant = "default",
  showImage = true
}: {
  children?: React.ReactNode;
  companyId: string;
  cardInfo?: {
    title: string;
    description: React.ReactNode;
    intent?: "neutral" | "danger" | "success" | "warn";
    badge?: string;
  };
  variant?: "unbranded" | "default";
  showImage?: boolean;
}) {
  const {
    data: company
  } = usePublicCompany(companyId);
  const {
    name,
    logo
  } = company || {};
  const companyImageUrl = getValidCompanyUrl(logo);
  const image = showImage && companyImageUrl ? <div className="relative w-full max-w-[184px] md:max-w-[400px]">
        <Image alt={name ?? "Attached media"} src={companyImageUrl} width={400} height={400} className="w-full h-auto max-h-[84px] md:max-h-[200px] object-contain" priority={true} />
      </div> : null;
  const badge = cardInfo?.badge ? <div className="flex items-center gap-2">
      <Badge intent={cardInfo.intent} className="flex items-center gap-1">
        {cardInfo.intent === "danger" ? <Ban size={12} className="text-red-500" /> : null}
        {cardInfo.badge}
      </Badge>
    </div> : null;
  const titleColorClasses = cn("text-gray-500", variant === "unbranded" && cardInfo?.intent === "danger" ? "text-red-950" : "text-gray-800");
  const descriptionColorClasses = cn("text-gray-500", variant === "unbranded" && cardInfo?.intent === "danger" && "text-red-800");
  return <div className="flex min-h-screen flex-col md:flex-row" data-sentry-component="Layout5050" data-sentry-source-file="layout-50-50.tsx">
      {/* left on desktop, bottom on mobile */}
      {company ? <div className="w-full md:w-1/2 bg-[#14243E] flex items-start md:items-center justify-start md:justify-center py-8 md:py-0 order-last md:order-first">
          <div className="flex items-start md:items-center justify-start md:justify-center h-full w-full flex-col gap-1 px-4 md:px-0">
            <div className="flex flex-col gap-5 justify-start items-start">
              {image ? <Card className="hidden md:inline-flex shadow-sm p-3">
                  {image}
                </Card> : null}
              <div className="mx-2">
                <ContactCard company={company} invert showMobileActions={false} />
              </div>
            </div>
          </div>
        </div> : null}

      {/* right on desktop, top on mobile */}
      <div className={cn("flex flex-1 items-center md:items-center justify-center relative px-4", BG_BY_INTENT[cardInfo?.intent ?? "neutral"], "md:bg-custom-base-subtle")}>
        {/* mobile background */}
        {!cardInfo?.intent || cardInfo?.intent === "neutral" ? <div className="md:hidden absolute inset-0 z-0">
            <div className="h-2/3 bg-custom-base-subtle" />
            <div className="h-1/3 bg-custom-base" />
          </div> : null}

        <div className="flex flex-col items-start gap-2 pt-6 z-10">
          {image ? <Card className="md:hidden p-3 ml-4 shadow-none">{image}</Card> : null}

          <Card className={cn("hidden md:flex w-[480px] shadow-sm bg-custom-base p-4 md:p-12 gap-6 flex-col", cardInfo?.intent === "danger" && "bg-red-50")} data-sentry-element="Card" data-sentry-source-file="layout-50-50.tsx">
            <CardHeader className="" data-sentry-element="CardHeader" data-sentry-source-file="layout-50-50.tsx">
              {badge}
              <CardTitle className={cn(variant === "unbranded" ? "text-3xl font-medium" : "text-sm font-bold", titleColorClasses)} data-sentry-element="CardTitle" data-sentry-source-file="layout-50-50.tsx">
                {cardInfo?.title}
              </CardTitle>
              {variant === "default" ? <CardTitle className="text-4xl font-medium text-balance">
                  {name}
                </CardTitle> : null}
              <CardDescription className={cn(variant === "unbranded" ? "" : "text-sm", descriptionColorClasses)} data-sentry-element="CardDescription" data-sentry-source-file="layout-50-50.tsx">
                {cardInfo?.description}
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0" data-sentry-element="CardContent" data-sentry-source-file="layout-50-50.tsx">{children}</CardContent>
          </Card>

          <div className="flex flex-col gap-6 md:hidden">
            <div className="flex flex-col gap-1 px-4">
              {badge}
              <Body className={cn(variant === "unbranded" ? "text-3xl font-medium" : "text-sm font-bold", titleColorClasses)} data-sentry-element="Body" data-sentry-source-file="layout-50-50.tsx">
                {cardInfo?.title}
              </Body>
              {variant === "default" ? <Body className="text-4xl font-medium">{name}</Body> : null}
              <Body className={cn(variant === "unbranded" ? "" : "text-sm", descriptionColorClasses)} data-sentry-element="Body" data-sentry-source-file="layout-50-50.tsx">
                {cardInfo?.description}
              </Body>
            </div>

            {children ? <div className="bg-custom-base w-screen p-4 pb-12">
                {children}
              </div> : null}
          </div>
        </div>
      </div>
    </div>;
}